import { Typography } from "@mui/material";
import React from "react";
import { Datagrid, DateField, FunctionField, NumberField, ReferenceField, TextField, useListContext } from "react-admin";
import { FillinOrigin } from "../../models";
import { PERMISSIONS } from "../../constants";
import Permission from "../Permission";
import { formatAttendantLabel } from "../../utils";
import CheckChainUsesModule, { ModuleKey } from "../CheckChainUsesModule";
import LimitField from "../LimitField";

const FillinsDatagrids = ({ screenType = '' }) => {
  const infos = useListContext();

  let data: Array<{ id: string, [key: string]: string | number }> = [];
  let summaryData: Array<{ id: string, [key: string]: string | number }> = [];

  if (infos.data) {
    const fillinInfo = infos.data[0];

    if (fillinInfo) {
      data = fillinInfo.data;
      const keys = ['total_value', 'total_amount', 'total_fillins', 'total_discounts'];
      let total = { id: 'total' };

      keys.forEach(key => {
        total[key] = fillinInfo.fillinsByFuel.reduce((acc, value) => {
          return acc + Number(value[key]);
        }, 0);
      });

      if (total['total_fillins']) {
        total['average_amount'] = total['total_amount'] / total['total_fillins'];
        total['average_ticket'] = total['total_value'] / total['total_fillins'];
      }

      summaryData = [total, ...fillinInfo.fillinsByFuel.sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0))]
    }
  }

  return (
    <>
      <Typography sx={{
        margin: '10px 0',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>Totais</Typography>
      <Datagrid
        bulkActionButtons={false}
        style={{ marginBottom: 30 }}
        data={summaryData}
        rowClick={false}
      >
        <TextField
          source="name"
          label="Combustível"
          emptyText="Todos"
          textAlign="center"
          sortable={false}
        />
        <NumberField
          source="total_value"
          label="Total em Valor"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="total_amount"
          label="Total em Litros"
          textAlign="center"
          emptyText="0"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
        <TextField source="total_fillins" textAlign="center" label="Total de abastecimentos" sortable={false} />
        <NumberField
          source="total_discounts"
          label="Total de descontos"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="average_ticket"
          label="Ticket Médio"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="average_amount"
          label="Volume Médio"
          textAlign="center"
          emptyText="0"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }}
          sortable={false}
        />
      </Datagrid>
      <Typography sx={{
        margin: '10px 0',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>Abastecimentos</Typography>
      <Datagrid
        bulkActionButtons={false}
        resource={infos.resource}
        data={data}
        rowClick="show"
      >
        <NumberField source="amount" label="Quantidade em litros" textAlign="center" />
        <NumberField source="value" label="Valor pago" textAlign="center" options={{ style: 'currency', currency: 'BRL' }} />
        <ReferenceField
          label="Combustível"
          reference="fuels"
          source="fuelId"
          textAlign="center"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Posto"
          source="placeId"
          reference="stations"
          textAlign="center"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <DateField source="receivedAt" label="Recebido em" textAlign="center" showTime locales="pt-BR" />
        <FunctionField label="Desconto" textAlign="center" render={record => record && `${(record.oldPricePerLiter && record.pricePerLiter ? (record.oldPricePerLiter - record.pricePerLiter) * (record.amount || 0) : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`} />
        <ReferenceField source="discountId" reference="discounts" label="Código do desconto" textAlign="center" emptyText="--">
          <TextField source="identify" />
        </ReferenceField>
        <ReferenceField source="scoringId" reference="scorings" label="Código da pontuação" textAlign="center" emptyText="--" link="show">
          <TextField source="identify" />
        </ReferenceField>
            {
              CheckChainUsesModule({
                module: ModuleKey.LIMITS, children: (
                  <LimitField source="limitId" label="Limite" />
                )
              }) || <></>
            }
        <ReferenceField source="vehicleId" reference="vehicles" label="Veículo" textAlign="center" emptyText="--" link={false}>
          <FunctionField render={record => [record.plate, record.brand, record.model].filter(Boolean).join(' · ')} />
        </ReferenceField>
        <NumberField source="odometer" label="Odômetro (km)" textAlign="center" emptyText="--" />
        <FunctionField
          source="origin"
          label="Origem"
          textAlign="center"
          render={record => FillinOrigin.translateOrigin(record ? record.origin : null)}
        />
        {Permission({
          permission: PERMISSIONS.LIST_ATTENDANT,
          children: (
            <ReferenceField
              label="Frentista"
              source="attendantId"
              reference="attendants"
              textAlign="center"
              emptyText="Não identificado"
              link={false}
            >
              <FunctionField render={record => formatAttendantLabel(record)} />
            </ReferenceField>
          )
        })}
      </Datagrid>
    </>
  );
};

export default FillinsDatagrids;
